/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2018, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { Component } from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { loadModule } from './ModuleLoader';
import Measure from 'react-measure';
import { ProgressIndicator,Label } from '@ba/ba-ui-carbon-toolkit';
import { fromPromise, PENDING, REJECTED, FULFILLED  } from 'mobx-utils';
import StringResources from '../nls/StringResources';
import { observable } from 'mobx';
import classnames from 'classnames';
import uniq from '../util/uniq';
import union from '../util/union';

import './visContainer.scss';

const PROMPT_ERROR_MESSAGE = 'promptingIsDisabled';
const VIS_DISABLED_FOR_THUMBNAIL = {
	'com.ibm.vis.rave2bundletiledmap': 'dashboard-analytics/images/placeholders/map.svg',
	'com.ibm.vis.snippetlistbundle' : 'dashboard-analytics/images/placeholders/snippetlist.svg'
};

@observer
class VisualizationContainer extends Component {
	@observable progress;
	@observable placeholderIcon = null;
	constructor(props) {
		super(props);
		this.liveWidgetPreview = null;
	}

	_cleanSpec(spec) {
		if(!spec) {
			return;
		}
		return Object.assign({}, spec, {
			properties: uniq(union(this.props.additionalProperties, spec.properties || []), item => item.id)
		});
	}

	_loadLiveWidgetPreview() {
		return new Promise((resolve, reject) => {
			loadModule(['dashboard-analytics/preview/LiveWidgetPreview'], resolve, reject);
		});
	}

	_createLiveWidget(LiveWidgetPreview) {
		const payload = {
			node: this.el,
			dashboardApi: this.props.context.dashboardApi,
			spec: this._cleanSpec(this.props.context.spec),
			// TODO: Remove when the livewidget preview is updated to user the dashboardApi in the main options
			runtimeEnv: {
				dashboardApi: this.props.context.dashboardApi
			},
			featureSet: this.props.featureSet
		};
		payload.predictData = this.props.predictData;
		payload.tabNavigation = false;
		payload.isPredictPreview = this.props.isPredictPreview;
		const renderWidget = this._renderLiveWidget(LiveWidgetPreview, payload);

		this.progress = fromPromise( renderWidget );
		return renderWidget;
	}

	_renderLiveWidget(LiveWidgetPreview, payload) {
		this.liveWidgetPreview = new LiveWidgetPreview(payload);


		if (this._renderStaticPreview()) {
			this.renderPreviewPromise = Promise.resolve(true);
		} else {
			this.renderPreviewPromise = this.liveWidgetPreview.render()
				.then(() => {
					if (this.liveWidgetPreview.getTitle) {
						this.props.onTitleChange(this.liveWidgetPreview.getTitle());
					}
					return true;
				})
				.catch((error) => {
					const message = error && error.message;
					if (message && message === PROMPT_ERROR_MESSAGE) {
						return this._getPlaceHolderIcon(this.liveWidgetPreview.spec.visId)
							.then((placeholder) => {
								this.placeholderIcon = placeholder.placeholderIcon;
								this.props.onPromptingError();
								return true;
							});
					} else {
						this.props.logger && this.props.logger.warn(error);
						return false;
					}
				});
		}

		return this.renderPreviewPromise;
	}

	componentDidMount() {
		let prom = this._loadLiveWidgetPreview()
			.then( (LiveWidgetPreview) => {
				return this._createLiveWidget(LiveWidgetPreview);
			} );
		this._loadPreviewPromise = prom;
		prom.then( null, ()=> {});  //already handled in render when progress is rejected, close the promise chain
	}

	componentWillUnmount() {
		if (this.liveWidgetPreview && this.liveWidgetPreview.destroy) {
			this.renderPreviewPromise
				.then(() => {
					this.liveWidgetPreview.destroy();
				});
		}
		if (this.el && this.el.parentNode) {
			this.el.parentNode.removeChild(this.el);
		}
		this._loadPreviewPromise = null;
	}

	_getPlaceHolderIcon(visId) {
		const visDefinitions = this.liveWidgetPreview.runtimeEnv.dashboardApi.getFeature('VisDefinitions');
		if (visDefinitions) {
			return visDefinitions.loadById(visId);
		} else {
			return Promise.resolve(null);
		}
	}

	_renderStaticPreview() {
		const visId = this.props.context && this.props.context.spec && this.props.context.spec.visId;
		if (visId) {
			const staticIcon = VIS_DISABLED_FOR_THUMBNAIL[visId];
			if (staticIcon) {
				this.placeholderIcon = staticIcon;
				return true;
			}
		}
		return false;
	}

	render() {
		let progressView;
		const spinner =  <div className="vis-recommender__container__content__pending_or_fail__spinner">
			<ProgressIndicator size={this.props.spinnerSize}/>
		</div>;
		if ( !this.progress ){
			progressView = (<div className="vis-recommender__container__content__pending_or_fail">
				{spinner}
			</div>);
		} else if (this.progress.state === PENDING ) {
			// When progress state is pending set progressView to null because livewidget now handles the rendering state with a wave-like loading
			progressView = null;
		} else if (this.progress.state === REJECTED ){
			const message = this.progress.value && this.progress.value.message || StringResources.get('widget_recommender_fail');
			progressView = 	(<div className="vis-recommender__container__content__pending_or_fail">
				<Label label={message} ellipsis />
			</div>);
		} else if (this.progress.state === FULFILLED ){
			if (this.placeholderIcon) {
				progressView = 	(
					<div className="vis-recommender__container__content__placeHolderIcon">
						<img className="vis-recommender__container__content__placeHolderIcon__img" src={this.placeholderIcon}/>
					</div>
				);
			}
		}

		const classNames = classnames(
			'vis-recommender__container',
			{
				'vis-recommender__container__highlighted': this.props.isHighlighted
			}
		);

		return (
			<Measure
				onResize={() => this.onResize()}
			>
				{({ measureRef }) =>
					<div className={classNames} ref={measureRef}>
						<div className="vis-recommender__container__content" ref={el => { this.el = el; }}>
							{progressView}
						</div>
					</div>
				}
			</Measure>
		);
	}

	onResize() {
		if (this._isVisible()) {
			this.liveWidgetPreview && this.liveWidgetPreview.resize();
		}
	}

	_isVisible() {
		return this.progress &&
			this.progress.state === FULFILLED &&
			this.el &&
			this.el.offsetHeight > 0 &&
			this.el.offsetWidth > 0;
	}
}

VisualizationContainer.propTypes = {
	context: PropTypes.object.isRequired,
	onTitleChange: PropTypes.func,
	spinnerSize: PropTypes.oneOf(['small', 'normal', 'large']),
	featureSet: PropTypes.array,
	predictData: PropTypes.object,
	logger: PropTypes.object,
	isPredictPreview: PropTypes.bool,
	isHighlighted: PropTypes.bool,
	onPromptingError: PropTypes.func,
	additionalProperties: PropTypes.arrayOf(PropTypes.object)
};

VisualizationContainer.defaultProps = {
	onTitleChange: () => {},
	additionalProperties: [],
	isHighlighted: false,
	onPromptingError: () => {}
};

export default VisualizationContainer;



// WEBPACK FOOTER //
// ./src/js/lib/@waca/vis-recommender/src/js/preview/VisualizationContainer.js